import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowRight, Building2, GitBranch, Layers, LayoutGrid, Sparkles, Tags, Users } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Skeleton } from '@/components/insights/Skeleton';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import { Blueprint, BlueprintRegistry, blueprintsService } from '@/services/blueprints.service';
import { industryIcon, timeAgo } from '@/pages/industry-configs/industry-meta';
import { FAMILY_META, familyIcon } from './blueprint-meta';

export default function BlueprintsList() {
  const [items, setItems] = useState<Blueprint[] | null>(null);
  const [registry, setRegistry] = useState<BlueprintRegistry | null>(null);

  useEffect(() => {
    Promise.all([blueprintsService.list(), blueprintsService.registry()])
      .then(([b, r]) => { setItems(b); setRegistry(r); })
      .catch(() => setItems([]));
  }, []);

  return (
    <>
      <Topbar title="Blueprints" breadcrumb="Catalog / Blueprints" description="Ready-made CRM setups for an industry family." />
      <div className="mx-auto w-full max-w-[1400px] space-y-6 px-4 py-6 sm:px-7 sm:py-8">
        <div className="grid gap-3 rounded-2xl border border-border bg-surface p-5 shadow-soft md:grid-cols-3">
          <HowItWorks icon={<LayoutGrid size={16} />} title="Pages & words are live">
            Which pages a company sees and what things are called follow its family's blueprint the moment you save.
          </HowItWorks>
          <HowItWorks icon={<Layers size={16} />} title="Setup is copied once">
            Fields, pipeline, departments and sample services are copied when a company gets the industry — then it's theirs to edit.
          </HowItWorks>
          <HowItWorks icon={<Sparkles size={16} />} title="Re-apply only adds">
            After you grow a blueprint, re-apply it on a company to add what's new. Nothing the company changed or deleted comes back.
          </HowItWorks>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {items === null
            ? [0, 1].map((i) => <Skeleton key={i} className="h-[340px] w-full rounded-2xl" />)
            : items.map((b, i) => <FamilyCard key={b.familyKey} b={b} registry={registry} delay={i * 60} />)}
        </div>

        <p className="text-center text-xs text-text-muted">
          Every other industry runs the standard CRM. Per-type fields still live in{' '}
          <Link to="/industry-configs" className="font-medium text-primary hover:underline">Industry Configs</Link>.
        </p>
      </div>
    </>
  );
}

function HowItWorks({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</span>
      <div>
        <div className="text-sm font-semibold text-text-primary">{title}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{children}</p>
      </div>
    </div>
  );
}

function FamilyCard({ b, registry, delay }: { b: Blueprint; registry: BlueprintRegistry | null; delay: number }) {
  const Icon = familyIcon(b.familyKey);
  const industryPages = (registry?.pages ?? []).filter((p) => !p.general && b.pages[p.key]);
  const renamed = Object.keys(b.labels ?? {}).length;
  const stats = [
    { icon: <LayoutGrid size={14} />, value: industryPages.length, label: 'industry pages' },
    { icon: <Tags size={14} />, value: renamed, label: 'renamed words' },
    { icon: <Layers size={14} />, value: b.fields.length, label: 'fields' },
    { icon: <GitBranch size={14} />, value: b.pipeline?.stages?.length ?? 0, label: 'pipeline stages' },
    { icon: <Users size={14} />, value: b.departments.length, label: 'departments' },
    { icon: <Sparkles size={14} />, value: b.sampleServices.length, label: 'sample services' },
  ];
  return (
    <Link
      to={`/blueprints/${b.familyKey}`}
      className="fade-up group flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevated"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="h-1 bg-gradient-to-r from-primary via-primary/40 to-transparent" />
      <div className="flex items-start gap-4 p-5">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary">
          <Icon size={28} strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold tracking-tight text-text-primary">{b.name}</h2>
            <span className="rounded-md bg-surface-alt px-1.5 py-0.5 font-mono text-[11px] text-text-muted">v{b.version}</span>
          </div>
          <p className="mt-1 text-sm text-text-muted">{b.description || FAMILY_META[b.familyKey]?.tagline}</p>
        </div>
        <ArrowRight size={18} className="mt-1 shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>

      <div className="flex flex-wrap gap-1.5 px-5">
        {b.industries.map((k) => {
          const opt = INDUSTRY_OPTIONS.find((o) => o.value === k);
          const TIcon = industryIcon(k, opt?.group);
          return (
            <span key={k} className="inline-flex items-center gap-1 rounded-full border border-border bg-bg/60 px-2.5 py-1 text-xs font-medium text-text-secondary">
              <TIcon size={12} className="text-primary" /> {opt?.label ?? k}
            </span>
          );
        })}
        {b.industries.length === 0 && <span className="text-xs text-text-muted">No types yet — companies won't get this blueprint.</span>}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-px border-y border-border bg-border">
        {stats.map((s) => (
          <div key={s.label} className="bg-surface px-4 py-3">
            <div className="flex items-center gap-1.5 text-text-muted">{s.icon}<span className="text-lg font-semibold tabular-nums text-text-primary">{s.value}</span></div>
            <div className="mt-0.5 text-2xs text-text-muted">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3.5 text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <Building2 size={13} /> <span className={clsx('font-semibold tabular-nums', b.companies ? 'text-text-primary' : '')}>{b.companies ?? 0}</span> companies
        </span>
        <span>{b.updatedBy === 'seed' ? 'Starter content' : `Saved ${timeAgo(b.updatedAt)}`}</span>
      </div>
    </Link>
  );
}
